import { useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  FileText,
  Mail,
  Maximize2,
  Minimize2,
  X,
} from "lucide-react";
import { callApi } from "../api/api";
import { useAccess } from "../context/AccessContext";
import { queueDestinationOptions } from "../context/WorkspaceContext";
import { resolveAdlsPath } from "../utils/resolveAdlsPath";

const COMPLETE_DESTINATION = { value: "none", label: "None — Operations Complete" };

function destinationFromMapping(roleCode, options) {
  const code = String(roleCode || "").trim().toUpperCase().replace(/\s+/g, "_");
  if (!code) return null;
  const match = options.find((option) => {
    const value = option.value;
    return code === value
      || code.startsWith(`${value}_`)
      || code === `${value}S`
      || code.startsWith(`${value}S_`);
  });
  return match?.value || null;
}

function sanitizePolicyNumber(value) {
  return String(value || "").replace(/[^A-Za-z0-9\s]/g, "");
}

function normalizedTypeLabel(value) {
  return String(value || "").trim().toLowerCase().replace(/[_-]+/g, " ");
}

function isExactMiscLabel(value) {
  return normalizedTypeLabel(value) === "misc";
}

function selectedDocumentType(documentTypes, selected) {
  const value = normalizedTypeLabel(selected);
  if (!value) return null;
  return documentTypes.find((item) => normalizedTypeLabel(item.lookup_code) === value)
    || documentTypes.find((item) => normalizedTypeLabel(item.lookup_name) === value)
    || null;
}

function lobIsRequired(documentTypes, selected) {
  const match = selectedDocumentType(documentTypes, selected);
  if (!match) return !isExactMiscLabel(selected);
  const labels = [match.lookup_code, match.lookup_name]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  return !labels.length || !labels.every(isExactMiscLabel);
}

function resolveLobValue(lobs, stored) {
  const value = String(stored || "").trim();
  if (!value) return "";
  const normalized = normalizedTypeLabel(value);
  const match = lobs.find((item) => (
    normalizedTypeLabel(item.lookup_code) === normalized
    || normalizedTypeLabel(item.lookup_name) === normalized
  ));
  return match ? String(match.lookup_code || "").trim() || value : value;
}

const EVENT_COLOR = {
  ROUTED: "#2563eb",
  REROUTED: "#2563eb",
  ASSIGNED: "#2563eb",
  REASSIGNED: "#7c3aed",
  RETURNED: "#c2410c",
  COMPLETED: "#16a34a",
  NOTE: "#64748b",
};

const CHIP_STYLES = {
  Note: { background: "#f1f5f9", color: "#334155" },
  Refer: { background: "#fff7ed", color: "#c2410c" },
  Return: { background: "#fef2f2", color: "#dc2626" },
  Route: { background: "#eff6ff", color: "#2563eb" },
  Complete: { background: "#f0fdf4", color: "#16a34a" },
  Assign: { background: "#f5f3ff", color: "#6d28d9" },
};

function formatWhen(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function statusLabel(status) {
  if (!status) return "In Progress";
  return String(status)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function activityChip(event) {
  if (event.event_type === "NOTE") return "Note";
  if (event.event_type === "RETURNED") {
    return event.to_destination_team_code === "OPERATION" ? "Return" : "Refer";
  }
  if (event.event_type === "ROUTED" || event.event_type === "REROUTED") return "Route";
  if (event.event_type === "COMPLETED") return "Complete";
  if (event.event_type === "ASSIGNED" || event.event_type === "REASSIGNED") return "Assign";
  return statusLabel(event.event_type);
}

function activityTitle(event) {
  const team = String(event.to_destination_team_code || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  if (event.event_type === "ROUTED" || event.event_type === "REROUTED") {
    return team ? `Routed to ${team}` : "Routed";
  }
  if (event.event_type === "ASSIGNED") return "Assigned";
  if (event.event_type === "REASSIGNED") return "Reassigned";
  if (event.event_type === "RETURNED") {
    return event.to_destination_team_code === "OPERATION"
      ? "Returned to Operations"
      : "Referred";
  }
  if (event.event_type === "COMPLETED") return "Completed";
  if (event.event_type === "NOTE") return "";
  return statusLabel(event.event_type);
}

function fileKind(name) {
  const lower = String(name || "").toLowerCase();
  if (lower.endsWith(".eml")) return "html";
  if (/\.(png|jpe?g|gif|webp|bmp|heic|heif|tif|tiff)$/.test(lower)) return "image";
  if (lower.endsWith(".pdf")) return "pdf";
  return "file";
}

const emailBodyCache = new Map();
const EMAIL_BODY_CACHE_LIMIT = 12;

function rememberEmailBody(path, value) {
  if (emailBodyCache.has(path)) emailBodyCache.delete(path);
  emailBodyCache.set(path, value);
  while (emailBodyCache.size > EMAIL_BODY_CACHE_LIMIT) {
    emailBodyCache.delete(emailBodyCache.keys().next().value);
  }
}

async function loadPreview(instance, accounts, path) {
  const filesystemDefault = process.env.REACT_APP_ADLS_SEARCH_FILESYSTEM || "cfpuw";
  const { filesystem, path: filePath } = resolveAdlsPath(path, filesystemDefault);
  const lower = filePath.toLowerCase();
  const needsBlob = /\.(tif|tiff|eml|heic|heif)$/.test(lower);
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  if (lower.endsWith(".eml")) {
    const cached = emailBodyCache.get(path) || emailBodyCache.get(filePath);
    if (cached) return cached;
  }

  if (needsBlob) {
    const token = await instance.acquireTokenSilent({
      account: instance.getActiveAccount() || accounts?.[0],
      scopes: [`api://${process.env.REACT_APP_BACKEND_CLIENT_ID}/access_as_user`],
    });
    const view = lower.endsWith(".eml") ? "&view=body" : "";
    const response = await fetch(
      `${baseUrl}/api/adls/preview?filesystem=${encodeURIComponent(filesystem)}&path=${encodeURIComponent(filePath)}${view}`,
      { headers: { Authorization: `Bearer ${token.accessToken}` } },
    );
    if (!response.ok) throw new Error(`Failed to preview file (${response.status})`);
    if (lower.endsWith(".eml")) {
      const email = await response.json();
      const preview = { kind: "html", html: email.html || "", to: email.to || "", cc: email.cc || "" };
      rememberEmailBody(path, preview);
      if (filePath !== path) rememberEmailBody(filePath, preview);
      return preview;
    }
    const blob = await response.blob();
    const type = response.headers.get("content-type") || blob.type || "";
    return { kind: "blob", url: URL.createObjectURL(blob), type };
  }

  const response = await callApi(instance, accounts, "GET", "/api/adls/preview-url", null, {
    params: { filesystem, path: filePath },
  });
  if (!response?.data?.url) throw new Error("Preview URL missing");
  return { kind: "url", url: response.data.url, type: fileKind(filePath) };
}

export default function MetadataReviewPanel({
  row,
  readOnly = false,
  hasNext = false,
  onClose,
  onAdvance,
  origin = null,
}) {
  const { instance, accounts } = useMsal();
  const { departments } = useAccess();
  const teamOptions = useMemo(
    () => queueDestinationOptions(departments, ["OPERATION", "OPERATIONS"]),
    [departments],
  );
  const [motion, setMotion] = useState(() => {
    if (!origin) return "idle";
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return "idle";
    return "opening";
  });
  const advanceTimer = useRef(null);
  const [maximized, setMaximized] = useState(true);
  const [stage, setStage] = useState("edit");
  const [destination, setDestination] = useState(null);
  const [mappedRole, setMappedRole] = useState("");
  const [documentTypes, setDocumentTypes] = useState([]);
  const [lobs, setLobs] = useState([]);
  const [history, setHistory] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = useState(true);
  const [viewerIndex, setViewerIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    document_type: row?.document_type || "",
    policy_number: row?.policy_number || "",
    lob: row?.lob || "",
    agency_no: row?.agency_no || "",
    primary_insured: row?.primary_insured || "",
    review_comments: "",
  });

  useEffect(() => {
    setStage("edit");
    setError("");
    setDestination(null);
    setMappedRole("");
    setForm({
      document_type: row?.document_type || "",
      policy_number: row?.policy_number || "",
      lob: row?.lob || "",
      agency_no: row?.agency_no || "",
      primary_insured: row?.primary_insured || "",
      review_comments: "",
    });
    setViewerIndex(null);
  }, [row?.agency_no, row?.document_type, row?.lob, row?.msg_id, row?.policy_number, row?.primary_insured]);

  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  const files = row?.attachments || [];
  const emailFile = files.find((file) => file.type === "email");
  const attachmentFiles = files.filter((file) => file.type !== "email");
  const returned = row?.queue_status === "RETURNED";
  const destinationLabel = teamOptions.find((item) => item.value === destination)?.label
    || "the selected team";

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      callApi(instance, accounts, "GET", "/api/admin/lookup/document_type"),
      callApi(instance, accounts, "GET", "/api/admin/lookup/line_of_business"),
      callApi(instance, accounts, "GET", "/api/team-queue/mapping", null, {
        params: { document_type: row?.document_type || "" },
      }).catch(() => null),
      callApi(
        instance,
        accounts,
        "GET",
        `/api/team-queue/history/${row?.msg_id}`,
      ).catch(() => null),
    ]).then(([types, lobList, mapping, historyResponse]) => {
      if (cancelled) return;
      setDocumentTypes(types?.data?.data || []);
      setLobs(lobList?.data?.data || []);
      if (!readOnly) {
        setMappedRole(mapping?.data?.data?.role_code || "");
      }
      setHistory(historyResponse?.data?.data || []);
    });
    return () => {
      cancelled = true;
    };
  }, [accounts, instance, readOnly, row?.document_type, row?.msg_id]);

  useEffect(() => {
    if (readOnly) return;
    setDestination(destinationFromMapping(mappedRole, teamOptions));
  }, [mappedRole, readOnly, teamOptions]);

  const lobRequired = useMemo(
    () => lobIsRequired(documentTypes, form.document_type),
    [documentTypes, form.document_type],
  );
  const selectedLob = resolveLobValue(lobs, form.lob);
  const selectedLobInList = lobs.some((item) => item.lookup_code === selectedLob);

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const validate = () => {
    if (!form.document_type) return "Document Type is required";
    if (!form.policy_number.trim()) return "Policy Number is required";
    if (!/^[A-Za-z0-9\s]+$/.test(form.policy_number.trim())) {
      return "Policy Number can only contain letters, numbers, and spaces";
    }
    if (lobRequired && !selectedLob) return "Line of Business is required";
    if (
      form.document_type !== (row?.document_type || "") &&
      !form.review_comments.trim()
    ) {
      return "Notes are required when Document Type is changed";
    }
    if (!destination) return "Select a destination";
    return "";
  };

  const submit = async () => {
    const message = validate();
    if (message) {
      setError(message);
      setStage("edit");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await callApi(instance, accounts, "POST", "/api/team-queue/review", {
        msg_id: row.msg_id,
        destination,
        ...form,
        policy_number: form.policy_number.trim(),
        lob: selectedLob,
        business_area: destination === "UNDERWRITING" ? selectedLob : "",
      });
      setStage("advancing");
      clearTimeout(advanceTimer.current);
      advanceTimer.current = setTimeout(() => onAdvance?.(), 1600);
    } catch (saveError) {
      setError(saveError.message || "Failed to review the item");
      setStage("edit");
    } finally {
      setSaving(false);
    }
  };

  const formFields = (
    <>
      {returned && (
        <div style={styles.returnBanner}>
          <div style={styles.returnTitle}>
            <AlertTriangle size={14} />
            Returned from Underwriting
          </div>
          <p style={styles.returnText}>{row.return_reason || "No return reason was recorded."}</p>
        </div>
      )}

      <section>
        <h3 style={styles.sectionLabel}>Document Details</h3>
        <div style={styles.grid}>
          <label style={styles.field}>
            <span style={styles.fieldLabel}>Policy Number</span>
            <input
              value={form.policy_number}
              disabled={readOnly}
              onChange={(event) =>
                updateField("policy_number", sanitizePolicyNumber(event.target.value))
              }
              style={styles.input}
            />
          </label>
          <label style={styles.field}>
            <span style={styles.fieldLabel}>Document Type</span>
            <select
              value={form.document_type}
              disabled={readOnly}
              onChange={(event) => updateField("document_type", event.target.value)}
              style={styles.input}
            >
              <option value="">Select document type</option>
              {form.document_type &&
                !documentTypes.some((item) => item.lookup_code === form.document_type) && (
                  <option value={form.document_type}>{form.document_type}</option>
                )}
              {documentTypes.map((item) => (
                <option key={item.lookup_code} value={item.lookup_code}>
                  {item.lookup_name || item.lookup_code}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section>
        <h3 style={styles.sectionLabel}>Metadata Fields</h3>
        <div style={styles.grid}>
          <label style={styles.field}>
            <span style={styles.fieldLabel}>Line of Business</span>
            <select
              value={selectedLob}
              disabled={readOnly}
              onChange={(event) => updateField("lob", event.target.value)}
              style={styles.input}
            >
              <option value="">Select LOB</option>
              {selectedLob && !selectedLobInList && (
                <option value={selectedLob}>{selectedLob}</option>
              )}
              {lobs.map((item) => (
                <option key={item.lookup_code} value={item.lookup_code}>
                  {item.lookup_name || item.lookup_code}
                </option>
              ))}
            </select>
          </label>
          <label style={styles.field}>
            <span style={styles.fieldLabel}>Agency No</span>
            <input
              value={form.agency_no}
              disabled={readOnly}
              onChange={(event) => updateField("agency_no", event.target.value)}
              style={styles.input}
            />
          </label>
          <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
            <span style={styles.fieldLabel}>Primary Insured</span>
            <input
              value={form.primary_insured}
              disabled={readOnly}
              onChange={(event) => updateField("primary_insured", event.target.value)}
              style={styles.input}
            />
          </label>
          <label style={{ ...styles.field, gridColumn: "1 / -1" }}>
            <span style={styles.fieldLabel}>Notes</span>
            <textarea
              rows={3}
              value={form.review_comments}
              disabled={readOnly}
              placeholder="Add review notes..."
              onChange={(event) => updateField("review_comments", event.target.value)}
              style={{ ...styles.input, resize: "none" }}
            />
          </label>
        </div>
      </section>

      {!readOnly && (
        <section style={styles.destination}>
          <h3 style={styles.sectionLabel}>Destination Team</h3>
          <select
            value={destination || ""}
            onChange={(event) => setDestination(event.target.value || null)}
            style={{ ...styles.input, marginTop: 12 }}
          >
            <option value="">Select a destination</option>
            {teamOptions.filter((option) => option.value === "CLAIMS" || option.value === "UNDERWRITING").map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            <option value={COMPLETE_DESTINATION.value}>{COMPLETE_DESTINATION.label}</option>
          </select>
        </section>
      )}

      <section>
        <button
          type="button"
          style={styles.activityToggle}
          onClick={() => setActivityOpen((open) => !open)}
        >
          Activity
          <ChevronDown
            size={14}
            style={{ transform: activityOpen ? "rotate(180deg)" : "none" }}
          />
        </button>
        {activityOpen && (
          <div style={styles.activityList}>
            {history.length === 0 && (
              <div style={styles.hint}>No queue activity yet.</div>
            )}
            {[...history].reverse().map((event, index, events) => {
              const chip = activityChip(event);
              const title = activityTitle(event);
              return (
                <div key={event.team_queue_log_id} style={styles.event}>
                  <div style={styles.rail}>
                    <span style={{ ...styles.dot, background: EVENT_COLOR[event.event_type] || "#94a3b8" }} />
                    {index < events.length - 1 && <span style={styles.line} />}
                  </div>
                  <div style={styles.eventBody}>
                    <div style={styles.eventHeading}>
                      <span style={{ ...styles.eventChip, ...(CHIP_STYLES[chip] || CHIP_STYLES.Note) }}>
                        {chip}
                      </span>
                      {title && <span style={styles.eventTitle}>{title}</span>}
                    </div>
                    <div style={styles.eventMeta}>
                      {event.actor || "—"} · {formatWhen(event.created_at)}
                    </div>
                    {event.comment && !/^Business area:/i.test(event.comment) && (
                      <div style={styles.eventComment}>{event.comment}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
      {error && <div style={styles.error}>{error}</div>}
    </>
  );

  const messagePane = (
    <MessagePane
      instance={instance}
      accounts={accounts}
      row={row}
      emailFile={emailFile}
      attachmentFiles={attachmentFiles}
      attachmentsOpen={attachmentsOpen}
      onToggleAttachments={() => setAttachmentsOpen((open) => !open)}
      onOpenAttachment={setViewerIndex}
      compact={!maximized}
    />
  );

  const requestClose = () => {
    const full = maximized || stage !== "edit";
    if (!origin || motion === "idle" || !full) {
      onClose();
      return;
    }
    if (motion === "closing") return;
    setMotion("closing");
  };

  let body = null;
  if (stage === "advancing") {
    body = (
      <div style={styles.center}>
        <div style={{ ...styles.resultIcon, background: "#eff6ff", color: "#2563eb" }}>
          <Check size={28} />
        </div>
        <div style={styles.resultTitle}>
          {destination === "none" ? "Document verified" : `Routed to ${destinationLabel}`}
        </div>
        <div style={styles.hint}>
          {hasNext ? "Opening the next item" : "That was the last item in this list"}
        </div>
        <div style={styles.progressTrack}>
          <div style={styles.progressBar} />
        </div>
      </div>
    );
  } else {
    body = (
      <div style={styles.shell}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Metadata Review</h2>
            <div style={styles.subtitle}>
              {row?.policy_number || "N/A"} · {row?.document_type || "UNKNOWN"}
            </div>
          </div>
          <div style={styles.headerActions}>
            {returned && <span style={styles.returnChip}>Returned from UW</span>}
            <button
              type="button"
              title={maximized ? "Minimize panel" : "Expand for full review"}
              style={styles.iconButton}
              onClick={() => setMaximized((value) => !value)}
            >
              {maximized ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button type="button" style={styles.iconButton} onClick={requestClose}>
              <X size={16} />
            </button>
          </div>
        </div>
        {maximized ? (
          <div style={styles.columns}>
            <div style={styles.formColumn}>{formFields}</div>
            {messagePane}
          </div>
        ) : (
          <div style={{ ...styles.formColumn, width: "100%", minWidth: 0 }}>
            {formFields}
            {messagePane}
          </div>
        )}
        <div style={styles.footer}>
          <button type="button" style={styles.secondary} onClick={requestClose}>
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <div>
              {destination == null && (
                <button type="button" style={styles.disabled} disabled>
                  Select a destination
                </button>
              )}
              {destination === "none" && (
                <button type="button" style={styles.complete} disabled={saving} onClick={submit}>
                  Mark Complete
                </button>
              )}
              {destination && destination !== "none" && (
                <button
                  type="button"
                  style={styles.route}
                  disabled={saving}
                  onClick={submit}
                >
                  Route to {destinationLabel}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  const coversPortal = maximized || stage !== "edit";
  const placed = Boolean(origin) && (motion === "closing" || (motion !== "idle" && coversPortal));
  const frameClass = placed
    ? `review-open-from-row${motion === "opening" ? " is-opening" : ""}${motion === "closing" ? " is-closing" : ""}`
    : undefined;

  return (
    <div
      className={frameClass}
      style={placed ? {
        position: "fixed",
        zIndex: 400,
        display: "flex",
        background: "#fff",
        "--review-top": `${origin.top}px`,
        "--review-left": `${origin.left}px`,
        "--review-width": `${origin.width}px`,
        "--review-height": `${origin.height}px`,
      } : { ...styles.overlay, background: coversPortal ? "#fff" : "transparent" }}
      onTransitionEnd={(event) => {
        if (event.target !== event.currentTarget || event.propertyName !== "width") return;
        if (motion === "opening") setMotion("open");
        if (motion === "closing") onClose();
      }}
    >
      <style>{`
        @keyframes operations-review-advance {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
      {!coversPortal && (
        <button type="button" style={styles.backdrop} aria-label="Close review" onClick={requestClose} />
      )}
      <div style={{ ...styles.panel, width: coversPortal ? "100%" : 520 }}>
        {body}
      </div>
      {viewerIndex !== null && (
        <AttachmentViewer
          instance={instance}
          accounts={accounts}
          files={attachmentFiles}
          index={viewerIndex}
          policyNumber={row?.policy_number}
          onIndex={setViewerIndex}
          onClose={() => setViewerIndex(null)}
        />
      )}
    </div>
  );
}

function MessagePane({
  instance,
  accounts,
  row,
  emailFile,
  attachmentFiles,
  attachmentsOpen,
  onToggleAttachments,
  onOpenAttachment,
  compact,
}) {
  const [emailHtml, setEmailHtml] = useState("");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const cached = emailBodyCache.get(emailFile?.path);
    setEmailHtml(cached?.html || "");
    setEmailError("");
    if (!emailFile?.path || cached) return undefined;
    loadPreview(instance, accounts, emailFile.path)
      .then((preview) => {
        if (cancelled) return;
        if (preview.kind === "html") {
          setEmailHtml(preview.html);
        } else setEmailError("This email could not be shown inline.");
      })
      .catch((loadError) => {
        if (!cancelled) setEmailError(loadError.message || "Failed to load the email");
      });
    return () => {
      cancelled = true;
    };
  }, [accounts, emailFile?.path, instance]);

  return (
    <div style={compact ? styles.compactEmail : styles.emailPanel}>
      <div style={styles.emailHeader}>
        <div style={styles.mailIcon}>
          <Mail size={14} />
        </div>
        <div>
          <div style={styles.emailSubject}>{row?.email_subject || "No subject"}</div>
        </div>
      </div>
      <div style={compact ? styles.compactBody : styles.emailBody}>
        {emailHtml ? (
          <iframe title="Email" sandbox="" srcDoc={emailHtml} style={styles.emailFrame} />
        ) : (
          <p style={styles.hint}>{emailError || (emailFile ? "Loading email..." : "No email file on this record.")}</p>
        )}
      </div>
      <div style={styles.attachmentBox}>
        <button type="button" style={styles.activityToggle} onClick={onToggleAttachments}>
          Attachments ({attachmentFiles.length})
          <ChevronDown size={14} style={{ transform: attachmentsOpen ? "rotate(180deg)" : "none" }} />
        </button>
        {attachmentsOpen && (
          <div style={styles.chips}>
            {attachmentFiles.map((file, index) => (
              <button
                key={`${file.path}-${index}`}
                type="button"
                style={styles.chip}
                onClick={() => onOpenAttachment(index)}
              >
                <FileText size={12} />
                {file.name || "File"}
              </button>
            ))}
            {attachmentFiles.length === 0 && <span style={styles.hint}>No attachments</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentViewer({ instance, accounts, files, index, policyNumber, onIndex, onClose }) {
  const file = files[index];
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const blobUrl = useRef("");

  useEffect(() => {
    let cancelled = false;
    setPreview(null);
    setError("");
    if (!file?.path) return undefined;
    loadPreview(instance, accounts, file.path)
      .then((result) => {
        if (cancelled) {
          if (result.url?.startsWith("blob:")) URL.revokeObjectURL(result.url);
          return;
        }
        if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
        blobUrl.current = result.kind === "blob" ? result.url : "";
        setPreview(result);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message || "Failed to open the file");
      });
    return () => {
      cancelled = true;
    };
  }, [accounts, file?.path, instance]);

  useEffect(() => () => {
    if (blobUrl.current) URL.revokeObjectURL(blobUrl.current);
  }, []);

  const kind = preview?.kind === "html" ? "html" : fileKind(file?.name);

  return (
    <div style={styles.viewer}>
      <div style={styles.viewerBar}>
        <span style={styles.viewerTitle}>
          {file?.name || "File"}
          {policyNumber ? ` · ${policyNumber}` : ""}
        </span>
        <div style={styles.viewerActions}>
          <button type="button" style={styles.viewerButton} disabled={index === 0} onClick={() => onIndex(index - 1)}>
            ← Prev
          </button>
          <span style={styles.viewerCount}>{index + 1} / {files.length}</span>
          <button
            type="button"
            style={styles.viewerButton}
            disabled={index >= files.length - 1}
            onClick={() => onIndex(index + 1)}
          >
            Next →
          </button>
          <button type="button" style={styles.viewerButton} onClick={onClose}>
            <X size={14} /> Close
          </button>
        </div>
      </div>
      <div style={styles.viewerTabs}>
        {files.map((item, itemIndex) => (
          <button
            key={`${item.path}-${itemIndex}`}
            type="button"
            style={itemIndex === index ? styles.viewerTabActive : styles.viewerTab}
            onClick={() => onIndex(itemIndex)}
          >
            <FileText size={12} />
            {item.name || "File"}
          </button>
        ))}
      </div>
      <div style={styles.viewerStage}>
        {error && <div style={styles.viewerMessage}>{error}</div>}
        {!preview && !error && <div style={styles.viewerMessage}>Loading preview...</div>}
        {preview?.kind === "html" && (
          <iframe title={file?.name} sandbox="" srcDoc={preview.html} style={styles.viewerFrame} />
        )}
        {preview && kind === "image" && (
          <img src={preview.url} alt={file?.name || "Attachment"} style={styles.viewerImage} />
        )}
        {preview && preview.kind !== "html" && kind !== "image" && (
          <iframe title={file?.name} src={preview.url} style={styles.viewerFrame} />
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: "fixed", inset: 0, zIndex: 400, display: "flex", justifyContent: "flex-end" },
  backdrop: { position: "absolute", inset: 0, border: "none", background: "rgba(15, 23, 42, 0.3)", cursor: "pointer" },
  panel: { position: "relative", height: "100%", maxWidth: "100%", background: "#fff", boxShadow: "-12px 0 32px rgba(15, 23, 42, 0.16)", display: "flex" },
  shell: { display: "flex", flexDirection: "column", width: "100%", height: "100%" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "14px 24px", borderBottom: "1px solid #e2e8f0" },
  title: { margin: 0, fontSize: 14, color: "#0f172a" },
  subtitle: { fontSize: 12, color: "#64748b", marginTop: 2 },
  headerActions: { display: "flex", alignItems: "center", gap: 6 },
  iconButton: { border: "none", background: "transparent", color: "#94a3b8", borderRadius: 6, padding: 6, cursor: "pointer" },
  returnChip: { fontSize: 10, fontWeight: 700, color: "#c2410c", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 4, padding: "2px 8px" },
  columns: { display: "flex", flex: 1, minHeight: 0 },
  formColumn: { width: "38%", minWidth: 320, overflow: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 },
  emailPanel: { flex: 1, minWidth: 0, borderLeft: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", flexDirection: "column" },
  emailHeader: { display: "flex", gap: 10, padding: "14px 20px", borderBottom: "1px solid #e2e8f0", background: "#fff" },
  mailIcon: { width: 28, height: 28, borderRadius: "50%", background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  emailSubject: { fontSize: 12, fontWeight: 700, color: "#0f172a" },
  emailBody: { flex: 1, minHeight: 0, overflow: "hidden", background: "#fff" },
  emailFrame: { width: "100%", height: "100%", border: "none", background: "#fff" },
  compactEmail: { border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", background: "#fff" },
  compactBody: { height: 180, overflow: "hidden", background: "#fff" },
  attachmentBox: { borderTop: "1px solid #e2e8f0", background: "#fff", padding: "4px 12px 12px" },
  chips: { display: "flex", flexWrap: "wrap", gap: 6, padding: "0 8px 4px" },
  chip: { display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#475569", borderRadius: 999, padding: "4px 10px", cursor: "pointer", fontSize: 11 },
  sectionLabel: { margin: "0 0 10px", fontSize: 10, letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  fieldLabel: { fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" },
  input: { width: "100%", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", fontSize: 14, color: "#0f172a", background: "#fff" },
  destination: { borderTop: "1px solid #e2e8f0", paddingTop: 16 },
  hint: { margin: 0, fontSize: 12, color: "#94a3b8" },
  activityToggle: { width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", border: "none", background: "transparent", color: "#64748b", fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", padding: "8px 4px", cursor: "pointer" },
  activityList: { display: "flex", flexDirection: "column" },
  event: { display: "flex", gap: 12 },
  rail: { display: "flex", flexDirection: "column", alignItems: "center", width: 12, flexShrink: 0 },
  dot: { width: 8, height: 8, borderRadius: "50%", marginTop: 4 },
  line: { width: 1, flex: 1, background: "#e2e8f0", minHeight: 20, margin: "4px 0" },
  eventBody: { paddingBottom: 16, minWidth: 0 },
  eventHeading: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  eventChip: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "1px 7px",
    fontSize: 10,
    fontWeight: 600,
    lineHeight: "14px",
  },
  eventTitle: { fontSize: 12, fontWeight: 600, color: "#0f172a" },
  eventMeta: { marginTop: 2, fontSize: 11, color: "#94a3b8" },
  eventComment: {
    marginTop: 6,
    fontSize: 11,
    color: "#64748b",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 4,
    padding: "4px 8px",
  },
  returnBanner: { background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: 12 },
  returnTitle: { display: "flex", alignItems: "center", gap: 6, color: "#c2410c", fontSize: 12, fontWeight: 700 },
  returnText: { margin: "6px 0 0", fontSize: 12, color: "#92400e" },
  error: { color: "#b91c1c", fontSize: 13 },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 24px", borderTop: "1px solid #e2e8f0", background: "#f8fafc" },
  secondary: { border: "1px solid #e2e8f0", background: "#fff", color: "#475569", borderRadius: 6, padding: "8px 16px", cursor: "pointer" },
  primary: { border: "none", background: "#2563eb", color: "#fff", borderRadius: 6, padding: "8px 16px", cursor: "pointer" },
  complete: { border: "none", background: "#16a34a", color: "#fff", borderRadius: 6, padding: "8px 16px", cursor: "pointer" },
  route: { border: "none", background: "#c2410c", color: "#fff", borderRadius: 6, padding: "8px 16px", cursor: "pointer" },
  disabled: { border: "none", background: "#e2e8f0", color: "#94a3b8", borderRadius: 6, padding: "8px 16px" },
  center: { margin: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 12, textAlign: "center", padding: 24, width: "100%" },
  resultIcon: { width: 56, height: 56, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" },
  resultTitle: { fontSize: 16, fontWeight: 700, color: "#0f172a" },
  progressTrack: { width: 220, height: 4, borderRadius: 999, background: "#e2e8f0", overflow: "hidden", marginTop: 8 },
  progressBar: { height: "100%", background: "#2563eb", animation: "operations-review-advance 1.6s linear forwards" },
  viewer: { position: "fixed", inset: 0, zIndex: 500, background: "rgba(15, 23, 42, 0.92)", display: "flex", flexDirection: "column" },
  viewerBar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.12)" },
  viewerTitle: { color: "#fff", fontSize: 14, fontWeight: 600 },
  viewerActions: { display: "flex", alignItems: "center", gap: 8 },
  viewerButton: { display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid rgba(255,255,255,0.25)", background: "transparent", color: "rgba(255,255,255,0.85)", borderRadius: 6, padding: "6px 10px", cursor: "pointer" },
  viewerCount: { color: "rgba(255,255,255,0.5)", fontSize: 12 },
  viewerTabs: { display: "flex", gap: 6, padding: "8px 20px", overflow: "auto", borderBottom: "1px solid rgba(255,255,255,0.12)" },
  viewerTab: { display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: "rgba(255,255,255,0.55)", borderRadius: 6, padding: "6px 10px", cursor: "pointer", whiteSpace: "nowrap" },
  viewerTabActive: { display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "#fff", color: "#0f172a", borderRadius: 6, padding: "6px 10px", cursor: "pointer", whiteSpace: "nowrap" },
  viewerStage: { flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  viewerFrame: { width: "100%", height: "100%", border: "none", background: "#fff", borderRadius: 8 },
  viewerImage: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },
  viewerMessage: { color: "rgba(255,255,255,0.75)", fontSize: 14 },
};
